import {
  ChangeDetectionStrategy, Component, inject, signal, OnInit, PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TrimWhitespaceDirective } from '../../../core/directives/trim.directive';
import { trimFormGroup } from '../../../core/utils/form.utils';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { API_ROUTES } from '../../../core/constants/api-routes';
import { ApiService } from '../../../core/services/api.service';

const passwordMatchValidator: ValidatorFn = (control: AbstractControl) => {
  const pw = control.get('newPassword');
  const cpw = control.get('confirmPassword');
  if (!pw || !cpw) return null;
  return pw.value === cpw.value ? null : { passwordMismatch: true };
};

type PageState = 'verifying' | 'ready' | 'error' | 'success';

@Component({
  selector: 'app-accept-invitation',
  templateUrl: './accept-invitation.html',
  styleUrl: './accept-invitation.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, TrimWhitespaceDirective],
  host: { ngSkipHydration: 'true' },
})
export class AcceptInvitation implements OnInit {
  private fb          = inject(FormBuilder);
  private authService = inject(AuthService);
  private apiService  = inject(ApiService);
  private toastService= inject(ToastService);
  private route       = inject(ActivatedRoute);
  private router      = inject(Router);
  private platformId  = inject(PLATFORM_ID);

  readonly pageState    = signal<PageState>('verifying');
  readonly errorMessage = signal('');
  readonly submitError  = signal('');
  readonly email        = signal('');
  readonly isSubmitting = signal(false);
  readonly showPassword = signal(false);
  readonly showConfirmPassword = signal(false);

  /** True when the invite URL contains IsNewUser=False — skips the password form. */
  readonly isExistingUser   = signal(false);
  /** Family space name extracted from the verify-invitation response. */
  readonly familySpaceName  = signal('');
  /** True while accept/decline API call is in flight. */
  readonly isResponding     = signal(false);
  /** Error from the accept/decline API call. */
  readonly responseError    = signal('');
  /** Contextual success message (differs for accept vs. decline). */
  readonly successMessage   = signal('');

  private inviteToken      = '';
  private familySpaceToken = '';

  readonly form = this.fb.group(
    {
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12),
          Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/),
        ],
      ],
      confirmPassword: ['', Validators.required],
    },
    { validators: passwordMatchValidator }
  );

  get f() { return this.form.controls; }
  get passwordMismatch(): boolean {
    return !!this.form.errors?.['passwordMismatch'] && !!this.f['confirmPassword'].touched;
  }

  togglePassword()        { this.showPassword.update(v => !v); }
  toggleConfirmPassword() { this.showConfirmPassword.update(v => !v); }

  get passwordCriteria() {
    const val: string = this.f['newPassword'].value || '';
    return {
      minLength:  val.length >= 8,
      maxLength:  val.length <= 12 && val.length > 0,
      hasUpper:   /[A-Z]/.test(val),
      hasNumber:  /[0-9]/.test(val),
      hasSpecial: /[@$!%*?&]/.test(val),
    };
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Always clear any existing session when an invitation URL is opened,
    // so a previously-logged-in user doesn't accidentally accept under the wrong account.
    this.authService.logoutWithoutRedirect();

    this.inviteToken      = this.route.snapshot.queryParamMap.get('token') ?? '';
    this.familySpaceToken = this.route.snapshot.queryParamMap.get('fstoken') ?? '';
    const isNewUserParam  = this.route.snapshot.queryParamMap.get('IsNewUser') ?? '';

    // IsNewUser=False → existing user: must be logged in before showing accept/decline
    this.isExistingUser.set(isNewUserParam.toLowerCase() === 'false');

    if (!this.inviteToken || !this.familySpaceToken) {
      this.errorMessage.set('Invalid invitation link. Please request a new invite.');
      this.pageState.set('error');
      this.redirectToLoginAfterDelay();
      return;
    }

    if (this.isExistingUser()) {
      if (!this.authService.isLoggedIn()) {
        // Not logged in — save tokens and redirect to login first
        sessionStorage.setItem('fm_pending_invite_token',   this.inviteToken);
        sessionStorage.setItem('fm_pending_invite_fstoken', this.familySpaceToken);
        this.router.navigate(['/login']);
        return;
      }
      // Already logged in — show accept/decline UI directly, no verification call
      this.pageState.set('ready');
      return;
    }

    // IsNewUser=True — new user must set a password; verify the token first
    this.verifyInvitation();
  }

  private async verifyInvitation(): Promise<void> {
    const cacheKey = `fm_inv_cache_${this.inviteToken}`;

    try {
      const resp = await this.authService.acceptInvitation(this.inviteToken, this.familySpaceToken);
      if (resp.status) {
        const data = resp.data as any;
        let email = '';
        let spaceName = '';
        if (typeof data === 'string') {
          email = data;
        } else if (data && typeof data === 'object') {
          email     = data.email ?? data.userEmail ?? '';
          spaceName = data.familySpaceName ?? data.familyName ?? data.name ?? '';
        }
        this.email.set(email);
        this.familySpaceName.set(spaceName);
        // Cache so a re-visit (tab closed before password set) still shows the form
        sessionStorage.setItem(cacheKey, JSON.stringify({ email, spaceName }));
        this.pageState.set('ready');
      } else {
        // The backend marks the invitation on first call; a second visit before
        // the password is set returns "already accepted". Try the cache so the
        // user can still complete the password-set step.
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { email, spaceName } = JSON.parse(cached);
            this.email.set(email ?? '');
            this.familySpaceName.set(spaceName ?? '');
            this.pageState.set('ready');
            return;
          } catch { /* ignore parse errors — fall through to error state */ }
        }
        this.errorMessage.set(resp.message || 'This invitation link is invalid or has expired.');
        this.pageState.set('error');
        this.redirectToLoginAfterDelay();
      }
    } catch {
      this.errorMessage.set('Something went wrong. Please try again or request a new invite.');
      this.pageState.set('error');
      this.redirectToLoginAfterDelay();
    }
  }

  /** Called when the existing user clicks Accept or Decline. */
  async respondToInvitation(isAccept: boolean): Promise<void> {
    this.responseError.set('');
    this.isResponding.set(true);
    try {
      const resp = await this.apiService.request<any>(
        'POST',
        API_ROUTES.ACCEPT_EXISTING_USER_INVITATION,
        {
          token:             this.inviteToken,
          familySpaceToken:  this.familySpaceToken,
          isAccept,
        },
        { useToken: true, showToaster: false }
      );
      if (resp.status) {
        // Clean up any pending invite keys left from the login-redirect flow
        sessionStorage.removeItem('fm_pending_invite_token');
        sessionStorage.removeItem('fm_pending_invite_fstoken');
        // Always use explicit messages — never rely on resp.message for decline
        // because the API may return a misleading value.
        this.successMessage.set(
          isAccept
            ? (resp.message || 'You have successfully joined the family space!')
            : 'Invitation declined.'
        );
        this.pageState.set('success');
        this.toastService.success(this.successMessage());
        // Accept → go to the app; Decline → go back to login
        if (isAccept) {
          setTimeout(() => this.router.navigate(['/app/stories']), 2500);
        } else {
          setTimeout(() => this.router.navigate(['/login']), 2500);
        }
      } else {
        this.responseError.set(resp.message || 'Something went wrong. Please try again.');
      }
    } catch {
      this.responseError.set('Something went wrong. Please try again.');
    } finally {
      this.isResponding.set(false);
    }
  }

  async onSubmit(): Promise<void> {
    trimFormGroup(this.form);
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    this.submitError.set('');
    this.isSubmitting.set(true);
    const { newPassword } = this.form.value;

    try {
      const resp = await this.apiService.request<string>(
        'POST',
        API_ROUTES.SET_PASSWORD,
        { token: this.inviteToken, password: newPassword },
        { useToken: false, showToaster: false }
      );

      if (resp.status) {
        this.pageState.set('success');
        this.successMessage.set(resp.message || 'Password set successfully! Redirecting to login…');
        this.toastService.success(this.successMessage());
        // Clear the verification cache — password has been set successfully
        sessionStorage.removeItem(`fm_inv_cache_${this.inviteToken}`);
        this.redirectToLoginAfterDelay();
      } else {
        this.submitError.set(resp.message || 'Failed to set password. Please try again.');
      }
    } catch {
      this.submitError.set('Something went wrong. Please try again.');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private redirectToLoginAfterDelay(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => this.router.navigate(['/login']), 3000);
  }
}
